import { apiSlice } from '../api/apiSlice'
import { RequestProps } from '../../types'
import CostsResponse from '../../types/response/CostsResponse'
import Cost from '../../types/models/Cost'
import dayjs from 'dayjs'
import { changeEmptyStringsToNull, changeNullsToEmptyString } from '../../utils/falsyValuesConverter'

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getCosts: builder.query<CostsResponse, RequestProps>({
      query: (props: RequestProps) => ({
        url: 'costs',
        params: props,
      }),
      transformResponse: (responseData: CostsResponse) => {
        return responseData
      },
      providesTags: () => [{ type: 'Cost', id: 'LIST' }],
    }),

    getSum: builder.query<number, RequestProps>({
      query: (props: RequestProps) => ({
        url: 'costs/sum',
        params: props,
      }),
      transformResponse: (responseData: number) => {
        return responseData
      },
      providesTags: () => [{ type: 'Cost', id: 'LIST' }],
    }),

    getCostById: builder.query<Cost, string>({
      query: (id: string) => `/costs/${id}`,
      transformResponse: (responseData: Cost) => {
        return changeNullsToEmptyString(responseData)
      },
      providesTags: () => [{ type: 'Cost', id: 'LIST' }],
    }),

    createCost: builder.mutation({
      query: (cost: Partial<CostModify>) => ({
        url: 'costs',
        method: 'POST',
        body: changeEmptyStringsToNull(cost),
      }),
      invalidatesTags: () => [{ type: 'Cost', id: 'LIST' }],
    }),

    editCost: builder.mutation<Cost, EditArgs>({
      query: ({ id, cost }) => {
        const formattedCost = {
          ...cost,
          startDate: cost.startDate ? cost.startDate.format('YYYY-MM-DD') : null,
          endDate: cost.endDate ? cost.endDate.format('YYYY-MM-DD') : null,
        }
        let url = `/costs/${id}`
        return {
          url: url,
          method: 'PUT',
          body: changeEmptyStringsToNull(formattedCost),
        }
      },
      invalidatesTags: () => [{ type: 'Cost', id: 'LIST' }],
    }),

    deleteCost: builder.mutation<Cost, string>({
      query: (id: string) => ({
        url: `/costs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'Cost', id: 'LIST' }],
    }),

    getSumByType: builder.query<CostSumByType, RequestProps>({
      query: (props: RequestProps) => ({
        url: '/costs/sum-by-type',
        params: props,
      }),
      transformResponse: (responseData: CostSumByType) => {
        return responseData
      },
      providesTags: () => [{ type: 'Cost', id: 'LIST' }],
    }),

    duplicateCosts: builder.mutation({
      query: (costs: DuplicateCost) => ({
        url: '/costs/duplicate',
        method: 'POST',
        body: changeEmptyStringsToNull(costs),
      }),
      invalidatesTags: () => [{ type: 'Cost', id: 'LIST' }],
    }),
  }),
})

export type CostSumByType = {
  [key: string]: number
}

export type CostModify = {
  amount: number
  costType: string
  description: string | null
  quantity: string
  startDate: dayjs.Dayjs | null
  endDate: dayjs.Dayjs | null
  principal: string | null
  projectId: string | null
}

export type DuplicateCost = {
  costIds: string[]
  date: string
}

type EditArgs = {
  id: string
  cost: Partial<CostModify>
}

// Generated hooks
export const {
  useGetCostsQuery,
  useGetSumQuery,
  useCreateCostMutation,
  useEditCostMutation,
  useDeleteCostMutation,
  useGetSumByTypeQuery,
  useDuplicateCostsMutation,
} = extendedApiSlice
